import React, { useState, useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useCrypto } from '../context/CryptoContext';
import { CATEGORIES, getCoinInfo } from '../data/coinDatabase';
import Sparkline from '../components/Sparkline';
import CoinDetailModal from '../components/CoinDetailModal';
import {
    LuSearch,
    LuStar,
    LuTrendingUp,
    LuTrendingDown,
    LuRefreshCw,
    LuWifiOff,
    LuArrowUp,
    LuArrowDown,
    LuZap
} from 'react-icons/lu';

function CryptoMarket() {
    const { lang } = useLanguage();
    const { coins, loading, connected, error, favorites, toggleFavorite, searchCoins } = useCrypto();

    const [searchQuery, setSearchQuery] = useState('');
    const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [sortBy, setSortBy] = useState('dbIndex'); // dbIndex, price, priceChangePercent, quoteVolume
    const [sortOrder, setSortOrder] = useState('asc'); // asc, desc
    const [selectedCoin, setSelectedCoin] = useState(null);

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder(column === 'dbIndex' ? 'asc' : 'desc');
        }
    };

    const SortIcon = ({ column }) => {
        if (sortBy !== column) return null;
        return sortOrder === 'asc' ? <LuArrowUp className="sort-icon" /> : <LuArrowDown className="sort-icon" />;
    };

    const formatPrice = (price) => {
        if (price >= 1000) return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        if (price >= 1) return `$${price.toFixed(2)}`;
        if (price >= 0.01) return `$${price.toFixed(4)}`;
        return `$${price.toFixed(6)}`;
    };

    const formatVolume = (volume) => {
        if (volume >= 1e9) return `$${(volume / 1e9).toFixed(2)}B`;
        if (volume >= 1e6) return `$${(volume / 1e6).toFixed(2)}M`;
        if (volume >= 1e3) return `$${(volume / 1e3).toFixed(2)}K`;
        return `$${volume.toFixed(2)}`;
    };

    const getCoinIcon = (shortName) => {
        return `https://assets.coincap.io/assets/icons/${shortName.toLowerCase()}@2x.png`;
    };

    const handleIconError = (e) => {
        e.target.style.display = 'none';
        e.target.nextSibling.style.display = 'flex';
    };

    // Filter and sort coins
    const filteredCoins = useMemo(() => {
        let result = searchQuery ? searchCoins(searchQuery) : [...coins];

        if (showFavoritesOnly) {
            result = result.filter(c => favorites.includes(c.symbol));
        }

        // Category filter
        if (selectedCategory !== 'all') {
            result = result.filter(c => {
                const coinInfo = getCoinInfo(c.symbol);
                return coinInfo?.category === selectedCategory;
            });
        }

        // Sort
        result.sort((a, b) => {
            let aVal = a[sortBy];
            let bVal = b[sortBy];

            if (sortOrder === 'asc') {
                return aVal - bVal;
            } else {
                return bVal - aVal;
            }
        });

        return result;
    }, [coins, searchQuery, showFavoritesOnly, favorites, searchCoins, sortBy, sortOrder, selectedCategory]);

    return (
        <div className="crypto-market-page">
            <div className="page-header home-header">
                <div className="page-header-content">
                    <div className="page-title-group">
                        <div>
                            <h1 className="page-title">
                                {lang === 'tr' ? 'Kripto Piyasası' : 'Crypto Market'}
                            </h1>
                        </div>
                    </div>
                    <div className="page-actions">
                        {connected ? (
                            <span className="live-indicator">
                                <LuZap className="live-icon" />
                                <span>{lang === 'tr' ? 'Canlı Veri' : 'Live Data'}</span>
                            </span>
                        ) : (
                            <span className="status-badge disconnected">
                                <LuWifiOff /> {lang === 'tr' ? 'Bağlantı kesildi' : 'Disconnected'}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className="market-controls">
                <div className="search-box">
                    <LuSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder={lang === 'tr' ? 'Coin ara...' : 'Search coin...'}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <button
                    className={`filter-btn ${showFavoritesOnly ? 'active' : ''}`}
                    onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                >
                    <LuStar /> {lang === 'tr' ? 'Favoriler' : 'Favorites'}
                </button>
            </div>

            <div className="category-filters">
                {Object.entries(CATEGORIES).map(([key, value]) => (
                    <button
                        key={key}
                        className={`category-btn ${selectedCategory === key ? 'active' : ''}`}
                        onClick={() => setSelectedCategory(key)}
                    >
                        {lang === 'tr' ? value.label : value.labelEn}
                    </button>
                ))}
            </div>

            {error && (
                <div className="market-error">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="market-loading">
                    <LuRefreshCw className="spinning" />
                    <span>{lang === 'tr' ? 'Yükleniyor...' : 'Loading...'}</span>
                </div>
            ) : (
                <div className="market-table-container">
                    <table className="market-table">
                        <thead>
                            <tr>
                                <th className="th-favorite"></th>
                                <th className="th-rank sortable" onClick={() => handleSort('dbIndex')}>
                                    # <SortIcon column="dbIndex" />
                                </th>
                                <th className="th-name">{lang === 'tr' ? 'Coin' : 'Coin'}</th>
                                <th className="th-price sortable" onClick={() => handleSort('price')}>
                                    {lang === 'tr' ? 'Fiyat' : 'Price'} <SortIcon column="price" />
                                </th>
                                <th className="th-change sortable" onClick={() => handleSort('priceChangePercent')}>
                                    24h % <SortIcon column="priceChangePercent" />
                                </th>
                                <th className="th-sparkline">{lang === 'tr' ? 'Son 7g' : 'Last 7d'}</th>
                                <th className="th-high">{lang === 'tr' ? '24s Yüksek' : '24h High'}</th>
                                <th className="th-low">{lang === 'tr' ? '24s Düşük' : '24h Low'}</th>
                                <th className="th-volume sortable" onClick={() => handleSort('quoteVolume')}>
                                    {lang === 'tr' ? 'Hacim' : 'Volume'} <SortIcon column="quoteVolume" />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCoins.map((coin, index) => (
                                <tr key={coin.symbol} className={`coin-row ${coin.priceDirection === 'up' ? 'row-flash-green' : coin.priceDirection === 'down' ? 'row-flash-red' : ''}`} onClick={() => setSelectedCoin(coin)}>
                                    <td className="td-favorite">
                                        <button
                                            className={`favorite-btn ${favorites.includes(coin.symbol) ? 'active' : ''}`}
                                            onClick={(e) => { e.stopPropagation(); toggleFavorite(coin.symbol); }}
                                        >
                                            <LuStar />
                                        </button>
                                    </td>
                                    <td className="td-rank">{index + 1}</td>
                                    <td className="td-name">
                                        <div className="coin-info">
                                            <img
                                                src={getCoinIcon(coin.shortName)}
                                                alt={coin.name}
                                                className="coin-icon"
                                                onError={handleIconError}
                                            />
                                            <span className="coin-icon-fallback">{coin.shortName.slice(0, 2)}</span>
                                            <div className="coin-name-group">
                                                <span className="coin-symbol">{coin.shortName}</span>
                                                <span className="coin-full-name">{coin.name}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="td-price">
                                        {formatPrice(coin.price)}
                                    </td>
                                    <td className={`td-change ${coin.priceChangePercent >= 0 ? 'positive' : 'negative'}`}>
                                        {coin.priceChangePercent >= 0 ? <LuTrendingUp /> : <LuTrendingDown />}
                                        {coin.priceChangePercent >= 0 ? '+' : ''}{coin.priceChangePercent.toFixed(2)}%
                                    </td>
                                    <td className="td-sparkline">
                                        <Sparkline data={coin.priceHistory} />
                                    </td>
                                    <td className="td-high">{formatPrice(coin.high24h)}</td>
                                    <td className="td-low">{formatPrice(coin.low24h)}</td>
                                    <td className="td-volume">{formatVolume(coin.quoteVolume)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="market-footer">
                <span>{filteredCoins.length} {lang === 'tr' ? 'coin listelendi' : 'coins listed'}</span>
            </div>

            {selectedCoin && (
                <CoinDetailModal
                    coin={selectedCoin}
                    onClose={() => setSelectedCoin(null)}
                />
            )}
        </div>
    );
}

export default CryptoMarket;
